import React from 'react';
import { Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faClock, 
  faExclamationTriangle,
  faExclamationCircle,
  faInfoCircle,
  faCircle
} from '@fortawesome/free-solid-svg-icons';
import '../styles/IotMeterAlerts.css';


const AlertInstanceCard = ({ alert, meter }) => {
  // Determine escalation level and corresponding styles
  const getEscalationInfo = (level) => {
    switch(level) {
      case 3:
        return {
          className: 'alert-card-level-3',
          badgeVariant: 'danger',
          icon: faExclamationTriangle,
          iconColor: '#dc3545',
          label: 'Level 3 - Severe'
        };
      case 2:
        return {
          className: 'alert-card-level-2',
          badgeVariant: 'warning',
          icon: faExclamationCircle,
          iconColor: '#ffc107',
          label: 'Level 2 - Medium'
        };
      case 1:
        return {
          className: 'alert-card-level-1',
          badgeVariant: 'info',
          icon: faInfoCircle,
          iconColor: '#17a2b8',
          label: 'Level 1 - Low'
        };
      default:
        return {
          className: 'alert-card-level-0',
          badgeVariant: 'secondary',
          icon: faClock,
          iconColor: '#6c757d',
          label: 'Not Escalated'
        };
    }
  };

  const level = alert.lastEscalatedLevel >= 1 ? alert.lastEscalatedLevel : 0;
  const escalationInfo = getEscalationInfo(level);

  // Format dates
  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate time since opened
  const getTimeSince = (date) => {
    const now = new Date();
    const opened = new Date(date);
    const diffMs = now - opened;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ${diffHours % 24}h ago`;
    if (diffHours > 0) return `${diffHours}h ${diffMins % 60}m ago`;
    return `${diffMins}m ago`;
  };

  // Get meter KPI info
  const getMeterKpiInfo = () => {
    if (!meter) return null;
    
    const latestKpi = meter.financialYearKpis?.sort((a, b) => 
      b.financialYear - a.financialYear
    )[0];
    
    return latestKpi?.kpi;
  };

  const kpiInfo = getMeterKpiInfo();

  return (
    <div className={`alert-instance-card ${escalationInfo.className} mb-3`}>
      <div className="alert-card-stripe"></div>
      <div className="alert-card-content">
        <div className="alert-card-header">
          <div className="d-flex align-items-center gap-3">
            <div className="alert-icon">
              <FontAwesomeIcon 
                icon={escalationInfo.icon} 
                size="lg"
                style={{ color: escalationInfo.iconColor }}
              />
            </div>
            <div className="alert-main-info flex-grow-1">
              <div className="d-flex align-items-center gap-2 mb-1">
                <h6 className="mb-0 fw-bold">Meter: {meter.meterName}</h6>
                <Badge bg={escalationInfo.badgeVariant}>
                  {escalationInfo.label}
                </Badge>
                <Badge bg={alert.status === 'OPEN' ? 'danger' : 'success'}>
                  {alert.status}
                </Badge>
              </div>
              <div className="text-muted small">
                Meter ID: {alert.meterId}
              </div>
            </div>
          </div>
        </div>

        <div className="alert-card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <div className="alert-info-item">
                <div className="alert-info-label">Opened At</div>
                <div className="alert-info-value">
                  <FontAwesomeIcon icon={faClock} className="me-2 text-muted" size="sm" />
                  {formatDate(alert.openedAt)}
                </div>
                <div className="alert-info-subtext">{getTimeSince(alert.openedAt)}</div>
              </div>
            </div>

            {kpiInfo && (
              <>
                <div className="col-md-2">
                  <div className="alert-info-item">
                    <div className="alert-info-label">Module</div>
                    <div className="alert-info-value">
                      <Badge bg="light" text="dark">{kpiInfo.module}</Badge>
                    </div>
                  </div>
                </div>

                <div className="col-md-2">
                  <div className="alert-info-item">
                    <div className="alert-info-label">Category</div>
                    <div className="alert-info-value">
                      <Badge bg="light" text="dark">{kpiInfo.category}</Badge>
                    </div>
                  </div>
                </div>

                <div className="col-md-2">
                  <div className="alert-info-item">
                    <div className="alert-info-label">Sub Category</div>
                    <div className="alert-info-value">
                      <Badge bg="light" text="dark">{kpiInfo.subCategory}</Badge>
                    </div>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="alert-info-item">
                    <div className="alert-info-label">KPI</div>
                    <div className="alert-info-value">
                      <Badge bg="primary">{kpiInfo.kpi}</Badge>
                    </div>
                  </div>
                </div>
              </>
            )}

            {!kpiInfo && (
              <div className="col-md-9">
                <div className="alert-info-item">
                  <div className="text-muted">Meter information not available</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {meter && (
          <div className="alert-card-footer">
            <div className="d-flex gap-3 text-muted small">
              {meter.meterType && (
                <span>
                  <FontAwesomeIcon icon={faCircle} className="me-1" size="xs" />
                  Type: {meter.meterType}
                </span>
              )}
              {meter.unit && (
                <span>
                  <FontAwesomeIcon icon={faCircle} className="me-1" size="xs" />
                  Unit: {meter.unit}
                </span>
              )}
              {meter.measurementType && (
                <span>
                  <FontAwesomeIcon icon={faCircle} className="me-1" size="xs" />
                  Measurement: {meter.measurementType}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertInstanceCard;